/**
 * Course monthly-plan change experiment (upgrade / downgrade).
 *
 * Problem this measures: a student on an active monthly plan who runs out of
 * credits is only offered a one-time credit top-up, so the cheapest plan plus
 * repeated top-ups behaves as a workaround for moving up a tier. Revenue still
 * arrives but it is not recurring, and the demand for a bigger (or smaller)
 * plan is never captured anywhere.
 *
 * There is no backend plan-change API yet, so the test variant captures the
 * request as intent — the ops team applies the change in Stripe — while the
 * control keeps today's top-up-only behaviour untouched.
 */

/** PostHog multivariate flag: 'control' | 'test'. */
export const COURSE_PLAN_CHANGE_FEATURE_FLAG = 'course_plan_change';

export const COURSE_PLAN_CHANGE_VARIANTS = {
  CONTROL: 'control',
  TEST: 'test',
} as const;

/** Which route the student took out of the plan-change screen. */
export const COURSE_PLAN_CHANGE_ROUTES = {
  UPGRADE: 'upgrade',
  TOPUP: 'topup',
  DOWNGRADE: 'downgrade',
} as const;

export type TCoursePlanChangeRoute = typeof COURSE_PLAN_CHANGE_ROUTES[keyof typeof COURSE_PLAN_CHANGE_ROUTES];

/**
 * Lessons-per-month tiers offered as a change target. Mirrors the preset plans
 * in MonthlyPlansTab (12 / 8 / 4) plus a 16 tier for students already at the
 * top. The frontend cannot know the student's current tier — `ICourse` only
 * exposes `hasActiveMonthlyPlan` — so the full ladder is shown for both
 * directions and the chosen route records the intent.
 */
export const COURSE_PLAN_CHANGE_TIERS: number[] = [4, 8, 12, 16];

/** Smallest plan on offer. A student already here has nothing to reduce to. */
export const COURSE_PLAN_CHANGE_MIN_TIER = COURSE_PLAN_CHANGE_TIERS[0];

/** Largest plan on offer. A student at or above this has nothing to move up to. */
export const COURSE_PLAN_CHANGE_MAX_TIER =
  COURSE_PLAN_CHANGE_TIERS[COURSE_PLAN_CHANGE_TIERS.length - 1];

/**
 * Bounds for the custom lessons-per-month slider on the plan-change screen.
 *
 * The ceiling mirrors CUSTOM_LESSONS_MAX in MonthlyPlansTab. The floor is the
 * smallest preset tier rather than that tab's CUSTOM_LESSONS_MIN of 6, because
 * a student reducing their plan must be able to reach the smallest plan the
 * product actually sells — stopping the slider at 6 would leave anyone on 5 or
 * fewer with no reachable option.
 *
 * Direction narrows these further at render time: an upgrade cannot go at or
 * below the current plan, and a reduction cannot go at or above it.
 */
export const COURSE_PLAN_CHANGE_CUSTOM_MIN = COURSE_PLAN_CHANGE_MIN_TIER;
export const COURSE_PLAN_CHANGE_CUSTOM_MAX = 50;

/**
 * Whether the plan-change screen still offers the one-time credit top-up.
 *
 * Turned off deliberately: the top-up is the workaround this experiment exists
 * to price out, and leaving it on the same screen as the upgrade lets students
 * keep taking it. The route, its tracking and the checkout it leads to are all
 * left intact so this is a one-line restore.
 *
 * While this is false a test-variant student with an active plan has no way to
 * buy extra credits mid-cycle — plan changes only take effect at their next
 * renewal. That is the intended trade-off, not an oversight.
 */
export const OFFER_TOPUP_ROUTE = false;

/**
 * Whether to offer the "reduce my plan" route when the student's current plan
 * size is unknown (the backend has not surfaced `currentLessonsPerMonth` yet).
 *
 * Showing it risks offering a reduction to someone already on the smallest
 * plan, where there is nothing to reduce to; hiding it removes the downgrade
 * arm of the experiment entirely. Kept as one switch so the trade-off is a
 * deliberate choice rather than an accident of missing data.
 */
export const SHOW_DOWNGRADE_WHEN_PLAN_SIZE_UNKNOWN = true;

export type TCoursePlanDowngradeReasonOption = {
  label: string;
  value: string;
};

/**
 * Why the student wants a smaller plan. Deliberately separates "this month is
 * quiet" (a pause-shaped need) from "too expensive" (a price objection) so the
 * retention response can differ.
 */
export const coursePlanDowngradeReasonOptions: TCoursePlanDowngradeReasonOption[] = [
  { label: 'Quieter month — fewer lessons needed', value: 'Quieter month' },
  { label: 'School holidays / travel', value: 'Holidays or travel' },
  { label: 'Too expensive at the current plan', value: 'Too expensive' },
  { label: 'Exams are over for now', value: 'Exams finished' },
  { label: 'Not using all my lessons', value: 'Unused lessons' },
  { label: 'Other', value: 'Other' },
];
